import React from "react";

import FetchHelper from "../../utils/FetchHelper";
import Notify from "../../utils/Notify";

const COLORS = [
  "#73113b",
  "#ff5e8a",
  "#3699ff",
  "#1bc5bd",
  "#ffa800",
  "#8950fc",
  "#f64e60",
  "#0bb783",
  "#6993ff",
  "#b5b5c3",
];

function normalizeCounty(value) {
  const county = String(value || "")
    .trim()
    .replace(/^county\s+/i, "")
    .replace(/^co\.\s*/i, "")
    .replace(/\s+/g, " ");

  if (!county) return "Unknown";

  return county
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getCustomerCounty(customer) {
  const address =
    customer?.address ||
    customer?.user?.address ||
    customer?.default_address ||
    customer?.data?.customer_address ||
    customer?.addresses?.[0];

  return normalizeCounty(
    customer?.county ||
      customer?.user?.county ||
      address?.county ||
      address?.administrative_area_level_2 ||
      address?.state ||
      address?.city
  );
}

function polarToCartesian(cx, cy, radius, angle) {
  const radians = ((angle - 90) * Math.PI) / 180;

  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

function createSlicePath(cx, cy, radius, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, radius, endAngle);
  const end = polarToCartesian(cx, cy, radius, startAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    `M ${cx} ${cy}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    "Z",
  ].join(" ");
}

export default class CustomerCountyPieChart extends React.Component {
  state = {
    loading: true,
    error: "",
    countyData: [],
    totalCustomers: 0,
  };

  componentDidMount() {
    this.loadCountyData();
  }

  buildPageUrl = (page) => {
    const separator = window.Api.Customers.includes("?") ? "&" : "?";

    return (
      `${window.Api.Customers}${separator}` +
      `page=${page}&page_size=100&pagination_type=page&order_by=-created_at`
    );
  };

  loadCountyData = async () => {
    this.setState({ loading: true, error: "" });

    try {
      const customers = [];
      let page = 1;

      while (true) {
        // Generate every URL from window.Api.Customers. Do not follow
        // response.next because the API may return an http:// URL, which an
        // HTTPS admin page cannot request because of browser access controls.
        const response = await FetchHelper.get(this.buildPageUrl(page), false);
        const pageCustomers = Array.isArray(response)
          ? response
          : Array.isArray(response?.results)
          ? response.results
          : [];

        customers.push(...pageCustomers);

        if (Array.isArray(response)) {
          break;
        }

        const totalCustomers = Number(response?.count) || 0;

        if (
          pageCustomers.length === 0 ||
          totalCustomers === 0 ||
          customers.length >= totalCustomers
        ) {
          break;
        }

        page += 1;
      }

      const uniqueCustomers = Array.from(
        new Map(
          customers.map((customer, index) => [
            String(customer?.user?.id ?? customer?.id ?? `customer-${index}`),
            customer,
          ])
        ).values()
      );

      const counts = uniqueCustomers.reduce((result, customer) => {
        const county = getCustomerCounty(customer);
        result[county] = (result[county] || 0) + 1;
        return result;
      }, {});

      const countyData = Object.entries(counts)
        .map(([county, count]) => ({ county, count }))
        .sort((a, b) => b.count - a.count || a.county.localeCompare(b.county));

      this.setState({
        loading: false,
        countyData,
        totalCustomers: uniqueCustomers.length,
      });
    } catch (error) {
      console.error("Failed to load customer county data:", error);
      Notify.error(error.message || "Failed to load customer county chart");
      this.setState({
        loading: false,
        error: "Could not load county information.",
      });
    }
  };

  renderChart() {
    const { countyData, totalCustomers } = this.state;
    let currentAngle = 0;

    if (!totalCustomers) {
      return <div className="text-muted text-center py-10">No customers found.</div>;
    }

    return (
      <div className="row align-items-center">
        <div className="col-lg-6 text-center mb-5 mb-lg-0">
          <svg
            viewBox="0 0 240 240"
            role="img"
            aria-label="Customers grouped by county"
            style={{ width: "100%", maxWidth: 320, height: "auto" }}
          >
            {countyData.map((item, index) => {
              const sliceAngle = (item.count / totalCustomers) * 360;
              const startAngle = currentAngle;
              const endAngle = currentAngle + sliceAngle;
              currentAngle = endAngle;

              if (sliceAngle >= 359.999) {
                return (
                  <circle
                    key={item.county}
                    cx="120"
                    cy="120"
                    r="100"
                    fill={COLORS[index % COLORS.length]}
                  />
                );
              }

              return (
                <path
                  key={item.county}
                  d={createSlicePath(120, 120, 100, startAngle, endAngle)}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#ffffff"
                  strokeWidth="2"
                >
                  <title>{`${item.county}: ${item.count}`}</title>
                </path>
              );
            })}

            <circle cx="120" cy="120" r="53" fill="#ffffff" />
            <text
              x="120"
              y="114"
              textAnchor="middle"
              fontSize="13"
              fill="#7e8299"
            >
              Customers
            </text>
            <text
              x="120"
              y="139"
              textAnchor="middle"
              fontSize="24"
              fontWeight="700"
              fill="#181c32"
            >
              {totalCustomers}
            </text>
          </svg>
        </div>

        <div className="col-lg-6">
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {countyData.map((item, index) => (
              <div
                key={item.county}
                className="d-flex align-items-center justify-content-between py-2 border-bottom"
              >
                <div className="d-flex align-items-center mr-3">
                  <span
                    className="mr-3"
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      flexShrink: 0,
                      backgroundColor: COLORS[index % COLORS.length],
                    }}
                  />
                  <span className="font-weight-bold">{item.county}</span>
                </div>

                <div className="text-right">
                  <strong>{item.count}</strong>
                  <span className="text-muted ml-2">
                    {((item.count / totalCustomers) * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { loading, error } = this.state;

    return (
      <div className="card card-custom mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">Customers by County</h3>
          </div>
        </div>

        <div className="card-body">
          {loading && (
            <div className="text-center py-10">
              <span className="spinner-border spinner-border-sm mr-2" />
              Loading county data...
            </div>
          )}

          {!loading && error && (
            <div className="text-danger text-center py-10">{error}</div>
          )}

          {!loading && !error && this.renderChart()}
        </div>
      </div>
    );
  }
}
